import type {
  WizardBaseAction,
  WizardNextStepAction,
  WizardRedirectAction,
  WizardRestartAction,
  WizardResyncStepAction,
} from '@lemonade-hq/chat-wizard-common-definitions';
import {
  next as wizardNext,
  redirect as wizardRedirect,
  restart as wizardRestart,
  skip as wizardSkip,
} from '@lemonade-hq/chat-wizard-common-definitions';
import type { GenericSchemaDataValue } from '@lemonade-hq/maschema-schema';
import type { PickRequired } from '@lemonade-hq/ts-helpers';
import type { NativeAppAction } from '@lemonade-hq/uniclient-mobile-bridge';
import type { IconName, Theme } from '@lemonade-hq/uniclient-theme';
import type { Method } from 'axios';
import type {
  UniclientCloseDialogAction,
  UniclientOpenDialogAction,
  UniclientOpenDynamicDialogAction,
  UniclientOpenToastAction,
  UniclientSaveDialogValuesAction,
} from './layouts';
import { closeDialog, openDialog, saveDialogValues } from './layouts';

export interface AutoTriggeringOptions {
  /** @optional The strategy to use for the auto trigger, by default it will trigger once */
  readonly strategy?: 'noInteractions' | 'once';
  /** The delay in milliseconds before the action is triggered */
  readonly delay: number;
}

export interface UniclientBaseAction<TType extends string> extends WizardBaseAction<TType> {
  /** @optional If true, the action will be displayed as a primary button */
  readonly primary?: boolean;
  /** @optional Will be shown in certain components (such as context menu, or header action) */
  readonly icon?: IconName;
  /** @optional adds an auto trigger to the action */
  readonly autoTrigger?: AutoTriggeringOptions | AutoTriggeringOptions[];
}

export type UniclientActionArray<T extends UniclientAction> = [PickRequired<T, 'content'>, ...T[]];

interface ExtendedSubmissionActionTracking {
  readonly tracking?: WizardBaseAction<string>['tracking'] & {
    readonly trackSubmissionSuccess?: boolean;
  };
}

// General actions
export type UniclientNextStepAction = ExtendedSubmissionActionTracking &
  Omit<WizardNextStepAction, 'redirectTo'> &
  UniclientBaseAction<'next'>;
export type UniclientResyncStepAction = ExtendedSubmissionActionTracking &
  UniclientBaseAction<'resync'> &
  WizardResyncStepAction;
export type UniclientRedirectAction = UniclientBaseAction<'redirect'> & WizardRedirectAction;
export type UniclientRestartAction = UniclientBaseAction<'restart'> & WizardRestartAction;
type ScrollBehavior = {
  /** @optional The behavior of the scroll, default is `smooth` */
  readonly behavior?: 'auto' | 'instant' | 'smooth';
};
type ScrollToIdProps = ScrollBehavior & {
  /** @optional The id of the element to scroll to */
  readonly toId: string;
  readonly toAttribute?: never;
};
type ScrollToAttributeProps = ScrollBehavior & {
  /** @optional Schema attribute reference to find target element */
  readonly toAttribute: string;
  readonly toId?: never;
};
export type UniclientScrollAction = UniclientBaseAction<'scroll'> & (ScrollToAttributeProps | ScrollToIdProps);
export type UniclientBackStepAction = UniclientBaseAction<'back'> & {
  /** @optional Number of levels to go back, default is 1 */
  readonly levels?: number;
};

interface UniclientChangeValueBase {
  /** Value to set */
  readonly value: GenericSchemaDataValue;
  /**
   * @optional Strategy to change the value, default is `replace`
   * @description
   * - `append`: appends the value to the current list
   * - `appendById`: appends or updates the value in the list by id
   * - `upsertById`: same as appendById with a better name :)
   * - `remove`: removes the value from the list
   * - `removeById`: removes the value from the list by id
   * - `replace`: replaces the value in the list
   * - `replaceById`: replaces the value in the list by id
   */
  readonly strategy?:
    | 'append'
    | 'appendById'
    | 'concat'
    | 'remove'
    | 'removeById'
    | 'replace'
    | 'replaceById'
    | 'upsertById';
}

export interface UniclientChangeValueAttribute<TComponentAttributes extends string = string>
  extends UniclientChangeValueBase {
  /** Attribute to set */
  readonly attribute: TComponentAttributes;
}

export interface UniclientChangeValueUiAttribute extends UniclientChangeValueBase {
  /** Ui Attribute to set */
  readonly uiAttribute: string;
}

export type UniclientChangeValue<TComponentAttributes extends string = string> =
  | UniclientChangeValueAttribute<TComponentAttributes>
  | UniclientChangeValueUiAttribute;

export type UniclientChangeValuesAction<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
> = UniclientBaseAction<'changeValues'> & {
  readonly data: UniclientChangeValue<TComponentAttributes | TUIAttributes>[];
};

export type UniclientOpenContextMenuAction<TComponentAttributes extends string = string> =
  UniclientBaseAction<'openContextMenu'> & {
    readonly items: UniclientAction<TComponentAttributes>[];
  };

export type UniclientOpenUrlAction = UniclientBaseAction<'openUrl'> & {
  /** The url to open */
  readonly url: string;

  /** @optional *Native only* If true, the url will be opened in an internal browser with a download option */
  readonly downloadable?: boolean;

  /** @optional *Native only* Specifies the file extension. */
  readonly fileExtension?: string;
};

interface UniclientDynamicListChangeValue<
  TTargetAttributes extends string = string,
  TSourceAttributes extends string = string,
> {
  /** The original schema attribute to be changed. */
  readonly targetAttribute: TTargetAttributes;
  /** The dialog schema attribute to take value from, will use the entire dialog answers as the value if left empty */
  readonly sourceAttribute?: TSourceAttributes;
  /**
   * @optional Strategy to change the value, default is `replace`
   * Look at `UniclientChangeValue['strategy']` for more details
   */
  readonly strategy?: UniclientChangeValue['strategy'];
  /**
   * @optional additional default values to save into the target attribute
   */
  readonly defaultValues?: Record<string, unknown>;
}

export type UniclientSaveDynamicListValuesAction<TComponentAttributes extends string = string> =
  UniclientBaseAction<'saveDynamicListValues'> & {
    readonly data: UniclientDynamicListChangeValue<TComponentAttributes>[];
  };

export type UniclientRemoveDynamicListValuesAction<TComponentAttributes extends string = string> =
  UniclientBaseAction<'removeDynamicListValues'> & {
    readonly data: UniclientDynamicListChangeValue<TComponentAttributes>[];
  };

export type UniclientInlineFormSkipAction = UniclientBaseAction<'inlineFormSkip'> & {
  readonly answersToInclude?: Record<string, string>;
};

export type UniclientCopyToClipboardAction = UniclientBaseAction<'copyText'> & {
  /** The text to copy */
  readonly text: string;
};

export type UniclientComposeAction<TComponentAttributes extends string = string> = UniclientBaseAction<'compose'> & {
  readonly actions: UniclientAction<TComponentAttributes>[];
};

export type UniclientNoopAction = UniclientBaseAction<'noop'>;

export type UniclientAsyncRequestAction = UniclientBaseAction<'asyncRequest'> & {
  readonly url: string;
  readonly initialValues?: Record<string, unknown>;
  readonly method?: Method;
  readonly headers?: Record<string, string>;
  readonly payloadMap: Record<string, string>;
  readonly successMessage?: string;
  readonly errorMessage?: string;
  readonly withCredentials?: boolean;
  readonly successAction?: UniclientAction;
  readonly errorAction?: UniclientAction;
  /**
   * @optional If true, the global loading state will be set to true when the request is made
   * and reset to false when the request is completed
   */
  readonly shouldSetGlobalLoading?: boolean;
};

export type UniclientOpenAUADialogAction = UniclientBaseAction<'openAUADialog'>;

export type UniclientScrollToHistoryAction = UniclientBaseAction<'scrollToHistory'>;

type NativeActionType = NativeAppAction['type'] | (string & {});

type NativeActionData<TActionType extends NativeActionType> = TActionType extends NativeAppAction['type']
  ? { readonly payload: (NativeAppAction & { readonly type: TActionType })['data'] }
  : { readonly payload?: Record<string, unknown> };

export type UniclientNativeAction<
  TActionType extends NativeActionType = NativeActionType,
  TData extends NativeActionData<TActionType> = NativeActionData<TActionType>,
> = TData &
  UniclientBaseAction<'nativeAction'> & {
    readonly actionType: TActionType;
  };

export type UniclientChangeThemeAction = Partial<Theme> & UniclientBaseAction<'changeTheme'>;

export type UniclientAction<TComponentAttributes extends string = string, TUIAttributes extends string = string> =
  | UniclientAsyncRequestAction
  | UniclientBackStepAction
  | UniclientChangeThemeAction
  | UniclientChangeValuesAction<TComponentAttributes, TUIAttributes>
  | UniclientCloseDialogAction
  | UniclientComposeAction
  | UniclientCopyToClipboardAction
  | UniclientInlineFormSkipAction
  | UniclientNativeAction
  | UniclientNextStepAction
  | UniclientNoopAction
  | UniclientOpenAUADialogAction
  | UniclientOpenContextMenuAction<TComponentAttributes>
  | UniclientOpenDialogAction<TComponentAttributes>
  | UniclientOpenDynamicDialogAction<TComponentAttributes>
  | UniclientOpenToastAction
  | UniclientOpenUrlAction
  | UniclientRedirectAction
  | UniclientRemoveDynamicListValuesAction<TComponentAttributes>
  | UniclientRestartAction
  | UniclientResyncStepAction
  | UniclientSaveDialogValuesAction<TComponentAttributes>
  | UniclientSaveDynamicListValuesAction<TComponentAttributes>
  | UniclientScrollAction
  | UniclientScrollToHistoryAction;

export type UniclientActionType = UniclientAction['type'];

export function next(props?: Omit<UniclientNextStepAction, 'consideredSubmission' | 'type'>): UniclientNextStepAction {
  return { primary: props?.primary, ...wizardNext(props) };
}

export function back({
  levels = 1,
  ...props
}: Omit<UniclientBackStepAction, 'consideredSubmission' | 'type'> = {}): UniclientBackStepAction {
  return { ...props, levels, consideredSubmission: false, type: 'back' };
}

export function skip(
  props?: Omit<UniclientNextStepAction, 'consideredSubmission' | 'enabledEvenIfInvalid' | 'skipAnswers' | 'type'>,
): UniclientNextStepAction {
  return {
    primary: props?.primary,
    tracking: { skipDefaultEvent: true, customEvent: props?.tracking?.customEvent ?? 'skip', ...props?.tracking },
    ...wizardSkip(props),
  };
}

export function redirect(props: Omit<UniclientRedirectAction, 'type'>): UniclientRedirectAction {
  return {
    primary: props.primary,
    tracking: {
      skipDefaultEvent: true,
      customEvent: props.tracking?.customEvent ?? 'redirect',
      ...props.tracking,
      customParams: { redirectUrl: props.url, ...props.tracking?.customParams },
    },
    ...wizardRedirect(props),
  };
}

export function restart(props: Omit<UniclientRestartAction, 'type'>): UniclientRestartAction {
  return { primary: props.primary, ...wizardRestart(props) };
}

export function scroll(
  props: Omit<UniclientBaseAction<'scroll'>, 'type'> & (ScrollToAttributeProps | ScrollToIdProps),
): UniclientScrollAction {
  return { ...props, type: 'scroll' };
}

export function scrollToHistory(
  props?: Omit<UniclientBaseAction<'scrollToHistory'>, 'type'>,
): UniclientScrollToHistoryAction {
  return { ...props, type: 'scrollToHistory' };
}

/**
 * @deprecated use the checkout page instead
 */
export function payment(): unknown {
  return { type: 'payment' };
}

export function changeValues<TComponentAttributes extends string = string>(
  props: Omit<UniclientChangeValuesAction<TComponentAttributes>, 'type'>,
): UniclientChangeValuesAction<TComponentAttributes> {
  return { ...props, type: 'changeValues' };
}

export function openContextMenu(props: Omit<UniclientOpenContextMenuAction, 'type'>): UniclientOpenContextMenuAction {
  return { ...props, type: 'openContextMenu' };
}

export function openUrl(props: Omit<UniclientOpenUrlAction, 'type'>): UniclientOpenUrlAction {
  return { ...props, type: 'openUrl' };
}

export function copyText(props: Omit<UniclientCopyToClipboardAction, 'type'>): UniclientCopyToClipboardAction {
  return { ...props, type: 'copyText' };
}

export function asyncRequest(
  props: Omit<UniclientAsyncRequestAction, 'initialValues' | 'type'>,
): UniclientAsyncRequestAction {
  return { ...props, type: 'asyncRequest' };
}

export function openAUADialog(props?: Omit<UniclientOpenAUADialogAction, 'type'>): UniclientOpenAUADialogAction {
  return { ...props, type: 'openAUADialog' };
}

/**
 * compose action composes multiple actions to one (ignores all presentational props of the actions)
 */
export function compose(props: Omit<UniclientComposeAction, 'type'>): UniclientComposeAction {
  return { ...props, type: 'compose' };
}

/**
 * noop action does nothing
 */
export function noop(props?: Omit<UniclientNoopAction, 'type'>): UniclientNoopAction {
  return { ...props, type: 'noop' };
}

/**
 * @warning this is highly discouraged, use only if you know what you are doing
 */
export function nativeAction<TActionType extends NativeActionType, TData extends NativeActionData<TActionType>>(
  props: Omit<UniclientNativeAction<TActionType, TData>, 'type'>,
): UniclientNativeAction<TActionType, TData> {
  return { ...props, type: 'nativeAction' } as UniclientNativeAction<TActionType, TData>;
}

export function changeTheme(props: Omit<UniclientChangeThemeAction, 'type'>): UniclientChangeThemeAction {
  return { ...props, type: 'changeTheme' };
}

export const ACTIONS = {
  next,
  back,
  skip,
  redirect,
  restart,
  scroll,
  payment,
  /**
   * @deprecated use step.layout.dialog.action
   */
  dialog: { open: openDialog, close: closeDialog, saveValues: saveDialogValues },
  changeValues,
  openUrl,
  asyncRequest,
  openAUADialog,
  compose,
  noop,
  copyText,
  contextMenu: { open: openContextMenu },
  scrollToHistory,
  nativeAction,
  changeTheme,
};
